// components/Section1.js
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
  Chip,
  Stack,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import EmojiObjectsIcon from "@mui/icons-material/EmojiObjects";
import StarsIcon from "@mui/icons-material/Stars";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import VerifiedIcon from "@mui/icons-material/Verified";
import SP from "../assets/HomePage/spark.png";
import Frame from "../assets/HomePage/Frame1.jpg";
import "./../styles/Section1.css";

const Section1 = () => {
  const nav = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const handleStartLearning = () => {
    nav("/usersignup");
  };

  const handleWatchDemo = () => {
    console.log("Watch demo clicked");
  };

  const features = [
    {
      icon: <SchoolIcon sx={{ fontSize: 28 }} />,
      title: "Expert Instructors",
      description: "Learn from industry professionals",
      color: "#4ECDC4",
      gradient: "linear-gradient(135deg, #4ECDC4, #45B7D1)",
    },
    {
      icon: <TrendingUpIcon sx={{ fontSize: 28 }} />,
      title: "Career Growth",
      description: "Advance your skills and career",
      color: "#FF6B6B",
      gradient: "linear-gradient(135deg, #FF6B6B, #FF8E53)",
    },
    {
      icon: <EmojiObjectsIcon sx={{ fontSize: 28 }} />,
      title: "Smart Learning",
      description: "AI-powered personalized paths",
      color: "#FFD93D",
      gradient: "linear-gradient(135deg, #FFD93D, #F6C90E)",
    },
  ];

  return (
    <Box
      className="hero-section"
      sx={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        background: "linear-gradient(135deg, #0a1628 0%, #1a2a4a 100%)",
        overflow: "hidden",
        pt: { xs: 10, md: 0 },
        pb: { xs: 6, md: 0 },
      }}
    >
      {/* Animated Background Elements */}
      <Box
        sx={{
          position: "absolute",
          top: "-20%",
          right: "-5%",
          width: "70%",
          height: "90%",
          background:
            "radial-gradient(circle, rgba(78, 205, 196, 0.08) 0%, transparent 70%)",
          borderRadius: "50%",
          zIndex: 0,
          animation: "pulse 8s ease-in-out infinite",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "-30%",
          left: "-10%",
          width: "60%",
          height: "70%",
          background:
            "radial-gradient(circle, rgba(69, 183, 209, 0.06) 0%, transparent 70%)",
          borderRadius: "50%",
          zIndex: 0,
          animation: "pulse 8s ease-in-out infinite 2s",
        }}
      />

      {/* Floating Particles */}
      {[...Array(6)].map((_, i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            top: `${10 + Math.random() * 80}%`,
            left: `${5 + Math.random() * 90}%`,
            width: `${4 + Math.random() * 6}px`,
            height: `${4 + Math.random() * 6}px`,
            borderRadius: "50%",
            backgroundColor:
              i % 2 === 0
                ? "rgba(78, 205, 196, 0.3)"
                : "rgba(69, 183, 209, 0.3)",
            animation: `float ${6 + Math.random() * 4}s ease-in-out infinite ${Math.random() * 3}s`,
            zIndex: 0,
          }}
        />
      ))}

      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        <Grid container spacing={4} alignItems="center">
          {/* Left Content */}
          <Grid item xs={12} md={6}>
            <Fade in timeout={800}>
              <Box>
                {/* Main Heading with Gradient */}
                <Typography
                  variant="h1"
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: "2.5rem", sm: "3.5rem", md: "4.5rem" },
                    lineHeight: 1.1,
                    color: "#ffffff",
                    mb: 1,
                  }}
                >
                  Welcome to
                  <Box
                    component="span"
                    sx={{
                      display: "block",
                      background:
                        "linear-gradient(135deg, #4ECDC4, #45B7D1, #4ECDC4)",
                      backgroundSize: "200% 200%",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      animation: "gradientMove 4s ease-in-out infinite",
                    }}
                  >
                    Wavepalm Techub
                  </Box>
                </Typography>

                {/* Sub Heading with Spark Animation */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 2,
                  }}
                >
                  <Typography
                    variant="h3"
                    component="h2"
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: "1.5rem", sm: "2.2rem", md: "2.8rem" },
                      color: "#ffffff",
                    }}
                  >
                    Learning at your{" "}
                    <Box
                      component="span"
                      sx={{
                        background: "linear-gradient(135deg, #b5fa3a, #9ae11e)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        position: "relative",
                      }}
                    >
                      Pace
                      <Box
                        component="img"
                        src={SP}
                        alt="spark"
                        sx={{
                          position: "absolute",
                          top: -25,
                          right: -35,
                          width: { xs: 30, sm: 40, md: 50 },
                          animation: "float 3s ease-in-out infinite",
                          filter:
                            "drop-shadow(0 0 20px rgba(255, 107, 107, 0.3))",
                        }}
                      />
                    </Box>
                  </Typography>
                </Box>

                {/* Description */}
                <Typography
                  variant="body1"
                  sx={{
                    fontSize: { xs: "1rem", md: "1.15rem" },
                    color: "rgba(255, 255, 255, 0.7)",
                    maxWidth: "90%",
                    mb: 4,
                    lineHeight: 1.8,
                  }}
                >
                  Get rid of the routine feeling from your learning process
                  while adding fun to it. Master new skills with interactive
                  courses designed by industry experts.
                </Typography>

                {/* CTA Buttons with New Design */}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={2}
                  alignItems="center"
                  sx={{ mb: 4 }}
                >
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleStartLearning}
                    startIcon={<RocketLaunchIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 700,
                      background: "linear-gradient(135deg, #4ECDC4, #45B7D1)",
                      borderRadius: 3,
                      minWidth: 200,
                      boxShadow: "0 4px 25px rgba(78, 205, 196, 0.3)",
                      "&:hover": {
                        transform: "translateY(-3px) scale(1.02)",
                        boxShadow: "0 8px 35px rgba(78, 205, 196, 0.4)",
                        background: "linear-gradient(135deg, #45B7D1, #4ECDC4)",
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Start Learning
                  </Button>

                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleWatchDemo}
                    startIcon={<PlayArrowIcon />}
                    sx={{
                      px: 4,
                      py: 1.5,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                      color: "#ffffff",
                      borderColor: "rgba(255, 255, 255, 0.25)",
                      borderRadius: 3,
                      "&:hover": {
                        borderColor: "#4ECDC4",
                        backgroundColor: "rgba(78, 205, 196, 0.08)",
                        transform: "translateY(-3px)",
                        boxShadow: "0 8px 30px rgba(78, 205, 196, 0.15)",
                      },
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                  >
                    Watch Demo
                  </Button>
                </Stack>

                {/* Trust Indicators */}
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={{ xs: 2, sm: 4 }}
                  sx={{
                    pt: 3,
                    borderTop: "1px solid rgba(255, 255, 255, 0.06)",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <VerifiedIcon sx={{ color: "#4ECDC4", fontSize: 20 }} />
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.5)" }}
                    >
                      Verified Certificate
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <StarsIcon sx={{ color: "#FFD93D", fontSize: 20 }} />
                    <Typography
                      variant="body2"
                      sx={{ color: "rgba(255,255,255,0.5)" }}
                    >
                      4.8/5 Average Rating
                    </Typography>
                  </Box>
                </Stack>

                {/* Feature Chips */}
                <Stack
                  direction="row"
                  spacing={1.5}
                  sx={{ mt: 3, flexWrap: "wrap", gap: 1 }}
                >
                  {features.map((feature, index) => (
                    <Chip
                      key={index}
                      icon={feature.icon}
                      label={feature.title}
                      sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.05)",
                        color: "#ffffff",
                        border: "1px solid rgba(255, 255, 255, 0.08)",
                        "&:hover": {
                          backgroundColor: "rgba(78, 205, 196, 0.15)",
                          borderColor: "#4ECDC4",
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                        "& .MuiChip-icon": {
                          color: feature.color,
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </Fade>
          </Grid>

          {/* Right Content - Image with Floating Cards */}
          <Grid item xs={12} md={6}>
            <Slide direction="left" in timeout={1000}>
              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    maxWidth: 550,
                    mx: "auto",
                  }}
                >
                  {/* Animated Decorative Rings */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: -30,
                      right: -30,
                      width: 120,
                      height: 120,
                      borderRadius: "50%",
                      border: "2px solid rgba(78, 205, 196, 0.15)",
                      zIndex: 0,
                      animation: "spin 20s linear infinite",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: -30,
                      left: -30,
                      width: 100,
                      height: 100,
                      borderRadius: "50%",
                      border: "2px solid rgba(69, 183, 209, 0.12)",
                      zIndex: 0,
                      animation: "spin 25s linear infinite reverse",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "110%",
                      height: "110%",
                      borderRadius: "50%",
                      border: "1px solid rgba(78, 205, 196, 0.05)",
                      zIndex: 0,
                      animation: "pulse 4s ease-in-out infinite",
                    }}
                  />

                  {/* Main Image with Glow Effect */}
                  <Box
                    sx={{
                      position: "relative",
                      borderRadius: 4,
                      overflow: "hidden",
                      boxShadow:
                        "0 20px 60px rgba(0, 0, 0, 0.5), 0 0 80px rgba(78, 205, 196, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.08)",
                      zIndex: 1,
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        inset: 0,
                        background:
                          "linear-gradient(135deg, rgba(78, 205, 196, 0.05), transparent 50%)",
                        zIndex: 1,
                      },
                    }}
                  >
                    <img
                      src={Frame}
                      alt="Learning platform"
                      style={{
                        width: "100%",
                        height: "auto",
                        display: "block",
                        transition:
                          "transform 0.7s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                      className="hero-image"
                    />
                  </Box>

                  {/* Floating Card 1 - Top Right (Courses) */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: { xs: -10, md: -60 },
                      right: { xs: 0, md: -25 },
                      backgroundColor: "rgba(10, 22, 40, 0.92)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(78, 205, 196, 0.2)",
                      borderRadius: 3,
                      p: 2.5,
                      minWidth: 140,
                      zIndex: 2,
                      animation: "float 4s ease-in-out infinite",
                      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "rgba(78, 205, 196, 0.15)",
                          borderRadius: "50%",
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <SchoolIcon sx={{ color: "#4ECDC4", fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255,255,255,0.4)" }}
                        >
                          Active Courses
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            color: "#ffffff",
                            fontWeight: 700,
                            background:
                              "linear-gradient(135deg, #4ECDC4, #45B7D1)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          200+
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Floating Card 2 - Bottom Left (Success Rate) */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: { xs: -10, md: -60 },
                      left: { xs: 0, md: -25 },
                      backgroundColor: "rgba(10, 22, 40, 0.92)",
                      backdropFilter: "blur(20px)",
                      border: "1px solid rgba(255, 215, 0, 0.15)",
                      borderRadius: 3,
                      p: 2.5,
                      minWidth: 140,
                      zIndex: 2,
                      animation: "float 4s ease-in-out infinite 1.5s",
                      boxShadow: "0 10px 40px rgba(0, 0, 0, 0.3)",
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          backgroundColor: "rgba(255, 215, 0, 0.15)",
                          borderRadius: "50%",
                          p: 1,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <EmojiObjectsIcon
                          sx={{ color: "#FFD93D", fontSize: 24 }}
                        />
                      </Box>
                      <Box>
                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255,255,255,0.4)" }}
                        >
                          Success Rate
                        </Typography>
                        <Typography
                          variant="h5"
                          sx={{
                            color: "#ffffff",
                            fontWeight: 700,
                            background:
                              "linear-gradient(135deg, #FFD93D, #F6C90E)",
                            WebkitBackgroundClip: "text",
                            WebkitTextFillColor: "transparent",
                          }}
                        >
                          94%
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Slide>
          </Grid>
        </Grid>
      </Container>

      {/* Scroll Indicator */}
      <Box
        sx={{
          position: "absolute",
          bottom: 30,
          left: "50%",
          transform: "translateX(-50%)",
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Typography
          variant="caption"
          sx={{
            color: "rgba(255,255,255,0.2)",
            fontSize: "0.65rem",
            letterSpacing: 2,
            textTransform: "uppercase",
          }}
        >
          Scroll
        </Typography>
        <Box
          sx={{
            width: 24,
            height: 40,
            border: "2px solid rgba(255, 255, 255, 0.15)",
            borderRadius: 12,
            position: "relative",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: 4,
              height: 10,
              background: "linear-gradient(135deg, #4ECDC4, #45B7D1)",
              borderRadius: 2,
              mt: 1.5,
              animation: "scrollDown 2s infinite",
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default Section1;
